import AccordionButton from '@/components/atoms/AccordionButton';
import BackLinkButton from '@/components/atoms/BackLinkButton';
import Button from '@/components/atoms/Button';
import ExternalLink from '@/components/atoms/ExternalLink';
import Heading from '@/components/atoms/Heading';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import useCallAPIGetCampaignInfo from '@/hooks/api/useCallAPIGetCampaignInfo';
import useGetCashbackInfo from '@/hooks/api/useGetCashbackInfo';
import useGetContractInfo from '@/hooks/api/useGetContractInfo';
import useSetLoading from '@/hooks/useSetLoading';
import alertState from '@/states/alertState';
import { CampaignInfoState } from '@/states/campaignInfoState';
import { CashbackInfoState } from '@/states/cashbackInfoState';
import { CashbackRegisterFormState } from '@/states/cashbackRegisterState';
import { CustomerInfoState } from '@/states/customerInfoState';
import { Campaign, CampaignInfoResponse } from '@/types/api/mypage/campaignInfo';
import { Cashback, CashbackInfoResponse } from '@/types/api/mypage/cashbackInfo';
import { ContractInfoResponse, ContractInfoType } from '@/types/api/mypage/contractInfo';
import { isExpiredToken } from '@/utils/cognito';
import { formatDate } from '@/utils/commonFunc';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import type { NextPage } from 'next';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

/**
 * キャンペーン詳細画面。
 * 契約情報・キャンペーン情報・キャッシュバック情報をまとめて取得し、
 * ユーザーが現在受けられる特典内容と申請導線を一画面で確認できるようにする。
 */
/**
 * 「キャンペーン内容」の表示／非表示を情報を取得する。
 * @param campaignInfo キャンペーン情報
 * @returns 表示可能な場合は、trueを返却する。それ以外は、falseを返却する。
 */
const isShowCampaign = (campaignInfo: CampaignInfoResponse): boolean => {
  if (!campaignInfo) {
    return false;
  }
  // 「キャンペーン内容」は「キャンペーン管理システム」を使用している場合のみ表示する。
  if (campaignInfo.is_customer_campaign) {
    return false;
  }
  if (campaignInfo?.campaigns?.length >= 1) {
    return true;
  }
  if (campaignInfo?.cashback_list?.length >= 1) {
    return true;
  }
  return false;
};

const CampaignDetail: NextPage = () => {
  /** 画面表示に必要な各種 API 呼び出し。 */
  const { execute: getContractInfo } = useGetContractInfo();
  const { execute: callAPIGetCampaignInfo } = useCallAPIGetCampaignInfo();
  const { execute: getCashbackInfo } = useGetCashbackInfo();
  /** 契約者名かななど、申請導線で再利用する情報を Recoil に保持する。 */
  const [customerInfo, setCustomerInfo] = useRecoilState<ContractInfoType>(CustomerInfoState);
  const campaignInfo = useRecoilValue(CampaignInfoState);
  const [cashbackInfo, setCashbackInfo] = useRecoilState<CashbackInfoResponse>(CashbackInfoState);
  const [cashbackRegisterForm, setCashbackRegisterForm] = useRecoilState(CashbackRegisterFormState);
  const setAlert = useSetRecoilState(alertState);
  const { startLoading, finishLoading } = useSetLoading();

  // 「キャンペーン内容」のアコーディオン状態
  const [campaignAccordionState, setCampaignAccordionState] = useState(false);

  useEffect(() => {
    // 特典の見せ分けには複数 API の結果が必要なため、初期表示でまとめて取得する。
    fetchCustomerInfo();
    callAPIGetCampaignInfo();
    fetchCashbackInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * キャッシュバック申請で必要になる契約者名かなと契約番号を補完する。
   */
  const fetchCustomerInfo = async () => {
    try {
      startLoading(ApiUrl.Read.CONTRACT_INFO);
      const { data }: AxiosResponse<ContractInfoResponse, any> = await getContractInfo();

      setCustomerInfo({
        ...customerInfo,
        contract_no: data.contract_no,
        last_name_kana: data.last_name_kana,
        first_name_kana: data.first_name_kana,
      });
    } catch (error: any) {
      if (isExpiredToken(error)) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.CONTRACT_INFO);
    }
  };

  /**
   * キャッシュバック申請可能状況の一覧を取得し、画面表示と次画面遷移に使う。
   */
  const fetchCashbackInfo = async () => {
    try {
      startLoading(ApiUrl.Read.CASHBACK_INFO);
      const { data }: AxiosResponse<CashbackInfoResponse, any> = await getCashbackInfo();

      setCashbackInfo(data);
    } catch (error: any) {
      if (isExpiredToken(error)) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.CASHBACK_INFO);
    }
  };

  /**
   * API のステータスコードを画面表示用の名称へ変換する。
   * 申請可否や手続き状況を利用者が読める文言に寄せるための変換層。
   */
  const getStatusName = (status: string): string => {
    const STATUS_CODE = Constants.Cashback.StatusCode;
    const STATUS_NAME = Constants.Cashback.StatusName;

    switch (status) {
      case STATUS_CODE.BEFORE_RECEIPT_PERIOD:
        return STATUS_NAME.BEFORE_RECEIPT_PERIOD;
      case STATUS_CODE.COMPLETE:
        return STATUS_NAME.COMPLETE;
      case STATUS_CODE.EXPIRES:
        return STATUS_NAME.EXPIRES;
      case STATUS_CODE.EXTINGUISHMENT_OF_RIGHTS:
        return STATUS_NAME.EXTINGUISHMENT_OF_RIGHTS;
      case STATUS_CODE.ERROR:
        return STATUS_NAME.ERROR;
      case STATUS_CODE.DURING_PROCEDURE:
        return STATUS_NAME.DURING_PROCEDURE;
      case STATUS_CODE.BEFORE_PROCEDURE:
        return STATUS_NAME.BEFORE_PROCEDURE;
      case STATUS_CODE.ACCOUNT_HOLDER_ILLEGAL:
        return STATUS_NAME.ACCOUNT_HOLDER_ILLEGAL;
      default:
        return '';
    }
  };

  /**
   * 選択したキャッシュバックコードを保持して申請入力画面へ遷移する。
   * 入力画面側は Recoil を前提にしているため、ここで事前にコードを格納する。
   */
  const toCashbackRegister = (cashbackCode: string) => {
    setCashbackRegisterForm({
      ...cashbackRegisterForm,
      code: cashbackCode,
    });

    Router.push('/campaign/cashback_register/');
  };

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>特典・キャンペーン</Heading>
        <div className='m-box-02'>
          <div className='m-box-02__inner'>
            <div className='m-heading-l2'>
              <h2 className='m-heading-l2__hdg'>キャンペーンの確認</h2>
            </div>
            <h3 className='m-heading-l3'>
              <span className='m-heading-l3__hdg'>キャンペーンに関する注意事項</span>
            </h3>
            <p className='m-text'>
              お支払方法がコンビニ払いの場合、キャッシュバックのお受け取りができません。キャッシュバック受取日・期間の前月までに、
              クレジットカードのご登録をお願いいたします。また、メールアドレスの登録もあわせてお願いいたします。
            </p>
            <div className='m-linkTxt'>
              <ExternalLink href='https://support.sonynetwork.co.jp/faqsupport/nuro/web/knowledge6963.html'>
                キャッシュバックの受け取り方法が知りたい方
              </ExternalLink>
            </div>
            {/** キャンペーン適用状況 */}
            {isShowCampaign(campaignInfo) && (
              <>
                <div className='m-heading-l3'>
                  <div className='accordion_container'>
                    <h3 className='m-heading-l3__hdg'>キャンペーン内容</h3>
                    <AccordionButton
                      label={{ open: '表示', close: '非表示' }}
                      isDisplay={campaignAccordionState}
                      onClick={() => setCampaignAccordionState(!campaignAccordionState)}
                    />
                  </div>
                </div>
                {campaignAccordionState && (
                  <>
                    {campaignInfo.cashback_list.map((item: Cashback) => {
                      return (
                        <div key={item.code}>
                          <table>
                            <colgroup>
                              <col className='w-30' />
                              <col />
                            </colgroup>
                            <tbody>
                              <tr>
                                <th className='m-table__th'>名称</th>
                                <td
                                  className='m-table__td'
                                  style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                                >
                                  {item.name}
                                </td>
                              </tr>
                              <tr>
                                <th className='m-table__th'>内容</th>
                                <td
                                  className='m-table__td'
                                  style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                                >
                                  {item.content}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <br />
                        </div>
                      );
                    })}
                    {campaignInfo.campaigns.map((item: Campaign) => {
                      return (
                        <div key={item.name}>
                          <table>
                            <colgroup>
                              <col className='w-30' />
                              <col />
                            </colgroup>
                            <tbody>
                              <tr key={`${item.name}name`}>
                                <th className='m-table__th'>名称</th>
                                <td
                                  className='m-table__td'
                                  style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                                >
                                  {item.name}
                                </td>
                              </tr>
                              <tr key={`${item.name}content`}>
                                <th className='m-table__th'>内容</th>
                                <td
                                  className='m-table__td'
                                  style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                                >
                                  {item.content}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <br />
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}

            {cashbackInfo.cashback_list.length !== 0 && (
              <>
                <h3 className='m-heading-l3 m-heading-l3__hdg'>キャッシュバック受取手続き</h3>
                <div className='cashback'>
                  <div
                    style={{
                      overflowX: 'auto',
                      width: '100%',
                    }}
                  >
                    <table className='m-table' style={{ width: '100%', marginBottom: '32px' }}>
                      <caption
                        style={{
                          textAlign: 'left',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                        }}
                      >
                        キャッシュバック受取手続き一覧
                      </caption>
                      <colgroup>
                        <col style={{ width: '200px' }} />
                        <col style={{ width: '150px' }} />
                        <col style={{ width: '200px' }} />
                        <col style={{ width: '200px' }} />
                        <col style={{ width: '110px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope='col' className='m-table__th'>
                            内容
                          </th>
                          <th scope='col' className='m-table__th'>
                            お手続き状況
                          </th>
                          <th scope='col' className='m-table__th' style={{ whiteSpace: 'normal' }}>
                            受取可能開始日
                          </th>
                          <th scope='col' className='m-table__th' style={{ whiteSpace: 'normal' }}>
                            受取可能終了日
                          </th>
                          <th scope='col' className='m-table__th'>
                            操作
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashbackInfo.cashback_list.map((cashback) => (
                          <tr key={cashback.code}>
                            <td
                              className='m-table__td'
                              style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                            >
                              {cashback.name}
                            </td>
                            <td className='m-table__td'>{getStatusName(cashback.status_code)}</td>
                            <td className='m-table__td'>
                              {formatDate(cashback.period.from)} 13:00
                            </td>
                            <td className='m-table__td'>{formatDate(cashback.period.to)}</td>
                            <td className='m-table__td'>
                              {cashback.can_receive_cashback && (
                                <div className='m-button col1' style={{ whiteSpace: 'nowrap' }}>
                                  <Button
                                    variant='secondary'
                                    onClick={() => toCashbackRegister(cashback.code)}
                                  >
                                    お手続き
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            <div className='m-button col2'>
              <BackLinkButton href='/campaign'>メニューに戻る</BackLinkButton>
            </div>
          </div>
        </div>
        <BreadcrumbLists />
      </main>
    </>
  );
};

export default CampaignDetail;
